/**
 * Best-effort ownership between DeepMox tabs. It intentionally has no
 * visibility/blur policy: background tabs keep playing until a newer explicit
 * playback action claims ownership. BroadcastChannel is preferred and the
 * storage event fallback covers browsers without it.
 */
const CHANNEL_NAME = 'deepmox-audio-owner-v1';
const STORAGE_KEY = 'deepmox.audio.owner.v1';
const tabId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
let lastEpoch = '';

type ReleaseOwner = () => void;
const owners = new Map<object, ReleaseOwner>();
let channel: BroadcastChannel | null | undefined;
let storageBound = false;

export function registerAudioPlaybackOwner(root: object, onRemoteClaim: ReleaseOwner): void {
	owners.set(root, onRemoteClaim);
	ensureTransport();
}

export function unregisterAudioPlaybackOwner(root: object): void {
	owners.delete(root);
}

export function claimAudioPlaybackOwner(root: object): void {
	if (!owners.has(root)) return;
	ensureTransport();
	const message = { type: 'claim', tabId, at: Date.now(), nonce: `${Date.now()}-${Math.random()}` };
	lastEpoch = epochOf(message);
	try { channel?.postMessage(message); } catch { /* fall through to storage */ }
	try { localStorage.setItem(STORAGE_KEY, JSON.stringify(message)); } catch { /* storage is optional */ }
}

function ensureTransport(): void {
	if (channel === undefined) {
		try {
			channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);
			if (channel) channel.onmessage = (event: MessageEvent<unknown>) => receive(event.data);
		} catch { channel = null; }
	}
	if (!storageBound && typeof window !== 'undefined') {
		storageBound = true;
		window.addEventListener('storage', (event) => {
			if (event.key !== STORAGE_KEY || !event.newValue) return;
			try { receive(JSON.parse(event.newValue)); } catch { /* ignore malformed external values */ }
		});
	}
}

function receive(value: unknown): void {
	if (!value || typeof value !== 'object') return;
	const message = value as { type?: unknown; tabId?: unknown; at?: unknown; nonce?: unknown };
	if (message.type !== 'claim' || message.tabId === tabId) return;
	const epoch = epochOf(message);
	if (!epoch || epoch <= lastEpoch) return;
	lastEpoch = epoch;
	for (const release of owners.values()) release();
}

function epochOf(message: { at?: unknown; nonce?: unknown }): string {
	if (!Number.isFinite(message.at) || typeof message.nonce !== 'string') return '';
	return `${String(Math.floor(message.at as number)).padStart(16, '0')}:${message.nonce}`;
}
